import {create} from 'zustand';
import axios from 'axios';
import {API_URL} from '../config/api';
import secureStorage from "../utils/secureStorage";

// Create axios instance
const api = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
    }
});

// Add auth header interceptor
api.interceptors.request.use(async config => {
    const token = await secureStorage.getItem('token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

const useAuthStore = create((set) => ({
    user: null,
    selectedAccount: null,
    token: null,
    isAuthenticated: false,
    loading: false,
    error: null,
    forgotPasswordStatus: null,
    isInitialized: false,

    // ✅ FIX: restore user + token + selectedAccount from secureStorage on reload
    initialize: async () => {
        try {
            const [token, user, selectedAccount] = await Promise.all([
                secureStorage.getItem('token'),
                secureStorage.getItem('user'),
                secureStorage.getItem('selectedAccount'),
            ]);

            set({
                token: token || null,
                isAuthenticated: !!token,
                user: user || null,               // ← was missing: permissions lost on reload
                selectedAccount: selectedAccount || null,
                isInitialized: true,
            });
        } catch (error) {
            console.error("Error initializing auth store:", error);
            set({isInitialized: true});
        }
    },

    login: async (email, password) => {
        set({loading: true, error: null});

        try {
            const {data} = await api.post('/auth', {email, password});

            if (data.token) {
                // ✅ FIX: persist user to secureStorage so it survives page reloads
                await Promise.all([
                    secureStorage.setItem('token', data.token),
                    secureStorage.setItem('user', data.user),
                    data.user?._id   ? secureStorage.setItem('_id', data.user._id)     : Promise.resolve(),
                    data.user?.email ? secureStorage.setItem('_email', data.user.email) : Promise.resolve(),
                ]);

                set({
                    isAuthenticated: true,
                    user: data.user,
                    token: data.token,
                });

                try {
                    const accountsResponse = await api.get('accounts', {
                        headers: {
                            'Accept': 'application/json',
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${data.token}`
                        }
                    });

                    const accounts = accountsResponse.data;
                    await secureStorage.setItem('accounts', accounts);

                    if (accounts.length === 1) {
                        await secureStorage.setItem('selectedAccount', accounts[0]);
                        set({selectedAccount: accounts[0]});

                        const {getFirstAccessibleRoute} = await import('../utils/permissionHelper');
                        const redirectTo = getFirstAccessibleRoute();
                        return {success: true, redirectTo};
                    }

                    return {success: true, redirectTo: '/select-account'};
                } catch (accountError) {
                    console.error("Error fetching accounts:", accountError);
                    return {success: true, redirectTo: '/dashboard'};
                }
            } else {
                set({error: "Login failed - No token received"});
                return {success: false};
            }
        } catch (error) {
            console.error('Login error:', error);
            set({error: error.response?.data?.message || "An error occurred during login"});
            return {success: false};
        } finally {
            set({loading: false});
        }
    },

    selectAccount: async (account) => {
        await secureStorage.setItem('selectedAccount', account);
        set({selectedAccount: account});
    },

    logout: async () => {
        await Promise.all([
            secureStorage.removeItem('token'),
            secureStorage.removeItem('user'),        // ✅ FIX: also clear user on logout
            secureStorage.removeItem('selectedAccount'),
            secureStorage.removeItem('accounts'),
            secureStorage.removeItem('_email'),
            secureStorage.removeItem('_id'),
        ]);
        localStorage.removeItem('token');
        set({isAuthenticated: false, user: null, selectedAccount: null, token: null});
    },

    forgotPassword: async (email) => {
        set({loading: true, error: null, forgotPasswordStatus: null});
        try {
            await api.post('/auth/forgot-password', {email});
            set({loading: false, forgotPasswordStatus: 'success'});
            return true;
        } catch (error) {
            set({
                error: error.response?.data?.message || "Erreur lors de l'envoi du mail",
                loading: false,
                forgotPasswordStatus: 'failed'
            });
            return false;
        }
    },

    resetPassword: async (token, newPassword) => {
        set({loading: true, error: null});
        try {
            await api.post('/auth/reset-password', {token, password: newPassword});
            set({loading: false});
            return {success: true};
        } catch (error) {
            set({
                error: error.response?.data?.message || 'Erreur lors de la réinitialisation du mot de passe',
                loading: false
            });
            return {success: false};
        }
    },

    clearErrors: () => set({error: null}),
    resetForgotPasswordStatus: () => set({forgotPasswordStatus: null}),
}));

// Initialize the store immediately on app load
(() => {
    const {initialize} = useAuthStore.getState();
    initialize();
})();

export default useAuthStore;
